import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";

import { API_URL } from "@/utils/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to send reset email");
      }
      
      setEmailSent(true);
      toast.success("Check your email for the reset link!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center font-['Inter'] bg-white px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-[#BFFF00] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#121212]" />
          </div>
          <h1 className="text-2xl font-bold text-[#121212] mb-4 font-['Manrope']">Check Your Email</h1>
          <p className="text-[#121212]/60 mb-6">
            If an account exists for <strong>{email}</strong>, you'll receive a password reset link shortly.
          </p>
          <p className="text-sm text-[#121212]/50 mb-8">
            Don't see it? Check your spam folder or try again.
          </p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => setEmailSent(false)}
              variant="outline"
              className="w-full rounded-full border-[#121212]"
            >
              Try Different Email
            </Button>
            <Link to="/login">
              <Button className="w-full rounded-full bg-[#121212] hover:bg-[#2a2a2a]">
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center font-['Inter'] bg-white px-4">
      <div className="max-w-md w-full">
        <Link to="/login" className="inline-flex items-center text-[#121212]/60 hover:text-[#121212] mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to login
        </Link>
        
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#121212] mb-2 font-['Manrope']">Forgot Password?</h1>
          <p className="text-[#121212]/60">No worries! Enter your email and we'll send you a reset link.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-[#121212]/70 font-medium">Email Address</Label>
            <div className="relative mt-2">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#121212]/40" />
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 pl-12 rounded-full border-[#BFFF00] focus:ring-[#BFFF00] bg-[#F7FFE5]/30"
                data-testid="forgot-email-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#121212] hover:bg-[#2a2a2a] text-white font-medium"
            data-testid="forgot-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Reset Link"
            )}
          </Button>
        </form>

        <p className="text-center text-[#121212]/60 mt-8">
          Remember your password?{" "}
          <Link to="/login" className="text-[#121212] font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
